import { firebaseAuth } from "../firebase/client-app";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import React, { createContext, useContext, useEffect, useState } from "react";
import { DbUser } from "../user/models";
import { userService } from "../user/service";
import { deleteCookie, setCookie } from "cookies-next";

interface UserContextType {
  user: DbUser | undefined;
  userLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<DbUser | undefined>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export default function UserProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<DbUser | undefined>(undefined);
  const [userLoading, setUserLoading] = useState(true);

  const setAuthCookie = async (idToken?: string) => {
    if (!idToken) {
      deleteCookie("Authorization");
      deleteCookie("selection");
    } else {
      setCookie("Authorization", `Bearer ${idToken}`);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, async (authUser) => {
      setUserLoading(true);

      if (authUser) {
        const fetchedUser = await userService.getUser(authUser.uid);
        setUser(fetchedUser ?? undefined);
      }

      setUserLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (authUser) => {
      if (authUser) {
        const token = await authUser.getIdToken();
        setAuthCookie(token);
      } else {
        setAuthCookie();
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    //refresh token every 5 minutes
    const interval = setInterval(
      async () => {
        try {
          if (firebaseAuth.currentUser) {
            const token = await firebaseAuth.currentUser.getIdToken(true);
            setAuthCookie(token);
          }
        } catch (error: any) {
          if (error.code === "auth/network-request-failed") {
            console.warn(
              "Network error during token refresh, will retry on next interval",
            );
          }
        }
      },
      4 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  return (
    <UserContext.Provider value={{ user, userLoading, setUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}
